// ====================================================================
// BAKED WORLD — load the committed edit-layer baseline at startup
// ====================================================================
// The editor's working edits live in localStorage (per-browser). To SHIP edits
// to kids, an author exports the edit layer to `world-baked.json` at the repo
// root and commits it. This module fetches that file at startup and hands the
// parsed layer back; main.js replays it onto the procedural world via the shared
// applyEditLayer iterator BEFORE chunks build, in ALL builds (no editor needed).
//
// Core-safe: no THREE, no DOM, no build flags. Fetch failures (no baseline
// committed yet → 404, or a parse error) resolve to null silently, so the
// no-build page and a fresh checkout never throw.
import { parse } from './editLayer.js';

// Relative URL works on the no-build page (served from repo root) and in the
// vite build (world-baked.json is copied to dist root via STATIC_COPY).
const BAKED_URL = './world-baked.json';

export async function loadBakedLayer() {
  try {
    // This call gates chunk build (main.js top-level awaits it), so a stalled
    // request on bad Wi-Fi must not hang startup. A ~4s AbortController timeout
    // lands in the catch below → null → the world builds procedurally. No retry:
    // the graceful-null path already exists and a timeout is enough here.
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 4000);
    let res;
    try {
      res = await fetch(BAKED_URL, { cache: 'no-cache', signal: ctrl.signal });
    } finally {
      clearTimeout(timer);
    }
    if (!res || !res.ok) return null;            // 404 before any baseline is committed
    const text = await res.text();
    if (!text || !text.trim()) return null;
    return parse(text);                          // throws on bad/stale version → caught below
  } catch {
    return null;                                 // abort/timeout lands here too
  }
}
